import React, { ChangeEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import {
  FormProvider,
  ValidatedSelect,
  ValidatedTextArea,
} from '@tq/ui-components/dist/components/forms-engine';
import { Box, AttentionMessage } from '@tq/ui-components';

import { useResponseTypes } from 'api';
import { CharacterCount } from 'components';
import {
  responseDetailState,
  responseTypeIdIsConsultedInErrorState,
  responseTypeIdState,
} from 'store';
import { contentWidth, selectComponentWidth } from '../../constants';
import { useResetResponseFormState, useResponseTypeDescription } from '../../hooks';
import { ConsultationResponseProps } from '../../types';
import { ResponseOverdueForm } from '..';

export const StandardResponseForm: React.FC<ConsultationResponseProps> = ({
  consultation,
  setUploadRequired,
}) => {
  const { t } = useTranslation('consultation_response');
  const { getResponseTypeDescription } = useResponseTypeDescription();
  const { data: responseTypesData } = useResponseTypes();

  const [responseTypeIdIsConsultedInError, setResponseTypeIdIsConsultedInError] =
    useRecoilState(responseTypeIdIsConsultedInErrorState);
  const [selectedResponseTypeId, setSelectedResponseTypeId] =
    useRecoilState(responseTypeIdState);
  const [responseDetail, setResponseDetail] = useRecoilState(responseDetailState);
  const { resetForm } = useResetResponseFormState();

  useEffect(
    () => {
      resetForm();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const responseType = selectedResponseTypeId
    ? responseTypesData?.responseTypes.find((type) => type.value === selectedResponseTypeId)
        ?.label
    : undefined;

  const responseTypeDescription = getResponseTypeDescription(responseType);

  useEffect(() => {
    setResponseTypeIdIsConsultedInError(
      selectedResponseTypeId ===
        responseTypesData?.responseTypes.find((r) => r.label === 'Consulted in error')?.value
    );
  }, [responseTypesData, selectedResponseTypeId, setResponseTypeIdIsConsultedInError]);

  const getSummaryTitle = responseTypeIdIsConsultedInError
    ? t('consulted_in_error_entry', 'Summary')
    : t('my_response_summary_entry', 'Summary (Optional)');

  const getSummaryRequiredStatus = responseTypeIdIsConsultedInError
    ? {
        required: {
          value: true,
          message: t('reason_text_required_error', 'Please enter reason'),
        },
      }
    : null;

  const handleOnChange = (value: string) => {
    setSelectedResponseTypeId(+value);
    const chosenResponse = responseTypesData?.responseTypes.find(
      (e) => e.value === parseInt(value)
    )?.documentsRequired;

    if (chosenResponse !== undefined) {
      setUploadRequired && setUploadRequired(chosenResponse);
    }
  };

  return (
    <FormProvider mode='onChange'>
      <Box pb={8}>
        {t(
          'set_response_type_summary',
          'Please select a consultation response type from the dropdown list below. This can be amended if necessary prior to submission of the consultation response.'
        )}
      </Box>
      <ValidatedSelect
        id='response-type-drop-down'
        aria-label={t('choose_response_type_label', 'Response type')}
        hideLabelColon={true}
        label={t('choose_response_type', 'Choose the response type')}
        mr={1}
        onChange={(value: string) => handleOnChange(value)}
        placeholder={`- ${t('select', 'Select')} -`}
        py={2}
        validation={{
          required: {
            value: true,
            message: t('response_type_required_error', 'Please select a response type'),
          },
        }}
        value={selectedResponseTypeId}
        w={{ base: '100%', lg: selectComponentWidth }}
      >
        {responseTypesData?.responseTypes.map((filter) => (
          <option key={filter.value} value={filter.value}>
            {filter.label}
          </option>
        ))}
      </ValidatedSelect>
      {selectedResponseTypeId && (
        <AttentionMessage
          status='info'
          title={`${responseType} ${t('response_type_definition_title', 'definition')}`}
          description={responseTypeDescription}
          mb={6}
          w='100%'
          maxWidth={{ base: '100%', lg: contentWidth }}
          className='attention-message-small'
        />
      )}
      <Box>
        <ValidatedTextArea
          id='responseTypeSummaryText'
          hideLabelColon={true}
          label={getSummaryTitle}
          mode='onChange'
          py={3}
          placeholder={t('summary_entry', 'Enter summary here')}
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
            setResponseDetail(event.target.value)
          }
          validation={{
            ...getSummaryRequiredStatus,
            maxLength: {
              value: 4000,
              message: t('error_text_length_error', 'You have exceeded the character limit'),
            },
          }}
          w='100%'
          maxWidth={{ base: '100%', lg: contentWidth }}
        />
        <CharacterCount value={responseDetail} maxCount={4000} />
      </Box>
      <ResponseOverdueForm consultation={consultation} />
    </FormProvider>
  );
};
